import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm, useFieldArray, Controller, FormProvider, DefaultValues } from 'react-hook-form';

import { ST, CountryEnum, Customer, CustomerCreateDTO, CustomerCreateSchema } from '_lib/api/apiTypes.shim';

import { Box } from '_lib/components/Box/Box';
import { Card } from '_lib/components/Card/Card';
import { CardContent } from '_lib/components/CardContent/CardContent';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { Form } from '_lib/components/Form/Form';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { Grid } from '_lib/components/Grid/Grid';
import { Radio } from '_lib/components/Radio/Radio';
import { RadioGroup } from '_lib/components/RadioGroup/RadioGroup';
import { Stack } from '_lib/components/Stack/Stack';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { Typography } from '_lib/components/Typography/Typography';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { BaseAddressForm } from '../FormItems/BaseAddressForm';
import { ContactListFormItem } from '../FormItems/ContactListFormItem';
import { LocationCreateFormItem } from '../FormItems/LocationCreateFormItem';

import type { ApiSubmitHandler } from '_lib/types/hookForm.types';

export interface CustomerFormProps<T extends Customer> {
  customer?: T;
  formId?: string;
  onCustomerSubmit: ApiSubmitHandler<CustomerCreateDTO>;
  contactsChild?: React.ReactElement;
  locationsChild?: React.ReactElement;
}

const defaultCustomerFormValues = (customer: Customer | undefined): DefaultValues<CustomerCreateDTO> =>
  customer
    ? {
        isDNS: customer.isDNS,
        isCommercial: customer.isCommercial,
        companyName: customer.companyName || '',
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        addressLine1: customer.addressLine1,
        addressLine2: customer.addressLine2,
        city: customer.city,
        stateOrProvince: customer.stateOrProvince,
        zipOrPostal: customer.zipOrPostal,
        country: customer.country,
        // if we are editing an existing customer, do not edit locations in the form
        useAddressForLocation: true,
        // if we are editing an existing customer, do not edit contacts, notes or locations
        contacts: undefined,
        locations: undefined,
        notes: undefined
      }
    : {
        isDNS: false,
        isCommercial: false,
        useAddressForLocation: true,
        contacts: [
          { type: ST.ContactTypeEnum.MobilePhone, value: '' },
          { type: ST.ContactTypeEnum.Email, value: '' }
        ],
        locations: [],
        notes: []
      };

export const CustomerForm = <T extends Customer>(props: CustomerFormProps<T>): React.ReactElement => {
  const { customer, formId, onCustomerSubmit, contactsChild, locationsChild } = props;

  const [nameCache, setNameCache] = useState({
    firstName: customer?.firstName,
    lastName: customer?.lastName,
    companyName: customer?.companyName
  });

  // TODO - discuss CustomerCreateDTO here (we might have an unseen issue with customer update?)
  const formContext = useForm<CustomerCreateDTO>({
    resolver: zodResolver(CustomerCreateSchema),
    defaultValues: defaultCustomerFormValues(customer),
    mode: 'onChange',
    shouldFocusError: true
  });

  const {
    handleSubmit,
    getValues,
    reset,
    control,
    setError,
    trigger,
    formState: { errors }
  } = formContext;

  const [previousCustomerId, setPreviousCustomerId] = useState<string | undefined>();
  if (customer?.providerId && customer.providerId !== previousCustomerId) {
    setPreviousCustomerId(customer.providerId);
    trigger(undefined, { shouldFocus: true });
  }

  const {
    fields: contactFields,
    append: contactAppend,
    remove: contactRemove
  } = useFieldArray({
    name: 'contacts',
    control
  });

  const {
    fields: locationFields,
    append: locationAppend,
    remove: locationRemove
  } = useFieldArray({
    name: 'locations',
    control
  });

  const {
    fields: noteFields,
    append: noteAppend,
    remove: noteRemove
  } = useFieldArray({
    name: 'notes',
    control
  });

  const customerIsCommercialChangeHandler = (isCommercial: boolean) => {
    const formValues = getValues();
    const { firstName: cacheFirstName, lastName: cacheLastName, companyName: cacheCompanyName } = nameCache;

    // Update name cache containing current form values before resetting form
    setNameCache({
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      companyName: formValues.companyName
    });

    if (isCommercial) {
      reset({
        ...formValues,
        isCommercial,
        firstName: null,
        lastName: null,
        companyName: cacheCompanyName || ''
      });
    } else {
      reset({
        ...formValues,
        isCommercial,
        firstName: cacheFirstName || '',
        lastName: cacheLastName || '',
        companyName: null
      });
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <FormProvider {...formContext}>
        <Form
          id={formId || 'st-api-call-pop-customer-form'}
          onSubmit={apiSubmitHandler(handleSubmit, setError, onCustomerSubmit, {
            resetForm: (resetData) => {
              // "reset" child forms first, then reset the parent with default values
              contactRemove();
              locationRemove();
              noteRemove();
              reset(resetData || defaultCustomerFormValues(undefined));
            }
          })}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12}>
              <RadioGroup
                name={'isCommercial'}
                row={true}
                onChange={(e) => customerIsCommercialChangeHandler(e.target.value === 'true')}>
                <FormControlLabel
                  checked={getValues('isCommercial') === false}
                  value={false}
                  control={<Radio />}
                  label='Residential'
                />
                <FormControlLabel
                  checked={getValues('isCommercial') === true}
                  value={true}
                  control={<Radio />}
                  label='Commercial'
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={6} sm={6}>
              <TextFieldFormItem
                name={'firstName'}
                label='First Name'
                control={control}
                required={getValues('isCommercial') === false}
                disabled={getValues('isCommercial') === true}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <TextFieldFormItem
                name={'lastName'}
                label='Last Name'
                control={control}
                required={getValues('isCommercial') === false}
                disabled={getValues('isCommercial') === true}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextFieldFormItem
                name={'companyName'}
                label='Company Name'
                control={control}
                required={getValues('isCommercial') === true}
                disabled={getValues('isCommercial') === false}
              />
            </Grid>
            <BaseAddressForm formContext={formContext} isNested={false} isEditingExisting={!!customer} />
            {!customer && (
              <>
                {/* If customer is not defined (i.e. no in passed context and we are instead creating a customer), show contact form here o.w. show standalone editor */}
                <Grid item xs={12} sm={12}>
                  <ContactListFormItem
                    contactFields={contactFields}
                    contactsPath={'contacts'}
                    contactAppend={contactAppend}
                    contactRemove={contactRemove}
                    requireOneContact={!customer}
                    oneContactTypes={[ST.ContactTypeEnum.MobilePhone, ST.ContactTypeEnum.Phone]}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    control={
                      <Controller
                        control={control}
                        name={'useAddressForLocation'}
                        defaultValue={false}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            color='primary'
                            checked={value}
                            onChange={(e) => {
                              onChange(e.target.checked);

                              if (e.target.checked) {
                                locationRemove(0);
                              } else {
                                // TODO - do this better when following issue is resolved: https://github.com/orgs/react-hook-form/discussions/10211
                                locationAppend({
                                  name: '',
                                  addressLine1: '',
                                  addressLine2: null,
                                  city: '',
                                  stateOrProvince: getValues('stateOrProvince'), // Default to stateOrProvince selected for customer
                                  zipOrPostal: '',
                                  country: CountryEnum.US,
                                  contacts: [{ type: ST.ContactTypeEnum.MobilePhone, value: '' }]
                                });
                              }
                            }}
                          />
                        )}
                      />
                    }
                    label={
                      <Typography
                        color={errors.useAddressForLocation ? 'error' : 'inherit'}
                        text='Use Address for Service Location?'
                      />
                    }
                  />
                </Grid>
                {locationFields[0] && (
                  <Grid item xs={12} sm={12}>
                    <Card variant='outlined'>
                      <CardContent>
                        <Typography variant='h3' text='Service Location' />
                        <Grid item xs={12} sm={12} sx={{ paddingTop: 1 }}>
                          {/** Note - this needs to be it's own component because conditional hooks in a single component is a no-no */}
                          <LocationCreateFormItem
                            locationFieldId={locationFields[0].id}
                            locationIndex={0}
                            formContext={formContext}
                          />
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        checked={noteFields.length > 0}
                        onChange={(e) => (e.target.checked ? noteAppend({ text: '', isPinned: false }) : noteRemove(0))}
                      />
                    }
                    label={<Typography sx={{ color: 'inherit' }} text='Add Note?' />}
                  />
                </Grid>
                {noteFields.length > 0 && (
                  <Grid item xs={12} sm={12}>
                    <TextFieldFormItem
                      multiline={true}
                      minRows={3}
                      maxRows={10}
                      name={`notes.0.text`}
                      label='Note'
                      required
                      control={control}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Form>
      </FormProvider>
      <Stack sx={{ marginTop: 2 }}>
        {contactsChild && <>{contactsChild}</>}
        {locationsChild && <>{locationsChild}</>}
      </Stack>
    </Box>
  );
};
